import { useQueryClient } from '@tanstack/react-query';

import { useMutation, useQuery } from '@hooks/reactQuery';

import { auth } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setSnackbar } from '@slices/common';

import {
	deleteOranThresholdPresetAPI,
	deleteThresholdPresetAPI,
	getPresetsThreshold,
	getThresholds,
	getThresholdTechnologies,
	getThresholdVendors,
	renameOranThresholdPreset,
	renamePresetThreshold,
	runExportOranThresholds,
	runExportThresholds,
	savePresetThreshold,
	setThresholds,
	updateOranThresholdPreset,
} from '@api/thresholdManagement';

import { extractQueryParams } from '@utils/common';

import { IEditThresholdDTO, IThresholdDTO } from '@interfaces/alarms/dto/thresholdDTO';
import { DefaultVendorOptions, IPresetThreshold, IThreshold } from '@interfaces/alarms/threshold';
import { TCMNodeVendorTechnology } from '@interfaces/cmModule/dto/cmModuleDTO';
import { IPresetThresholdCreateDTO, IPresetThresholdUpdateDTO } from '@interfaces/kpi/dto/kpiPresetsDTO';
import {
	IUpdateOranThresholdPreset,
} from '@components/routes/AdminPanel/KpiThresholdManagement/KpiThresholdsTechnology/interfaces';
import { TSelectOptionType } from '@components/common/Select/Select.types';

import { ESnackbarTypes, EUserRoles, EVendorsValues, TTechnologiesValues, TVendorsValues } from '@enums/common';

import { refetchOptions } from '@constants/common';
import {
	CREATE_PRESET_THRESHOLDS,
	DELETE_PRESET_THRESHOLDS,
	EDIT_PRESET_THRESHOLDS,
	EDIT_THRESHOLDS,
	EXPORT_THRESHOLDS,
	PRESETS_THRESHOLD,
	THRESHOLD_TECHNOLOGIES,
	THRESHOLD_VENDORS,
	THRESHOLDS,
} from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';

export const useThresholdManagement = (
	params: TCMNodeVendorTechnology<TVendorsValues | null, TTechnologiesValues | null>,
) => {
	const authState = useAppSelector(auth);
	const queryClient = useQueryClient();
	const dispatch = useAppDispatch();
	const {
		vendor,
		technology,
	} = params || {};

	const {
		accessPermission:
			{
				networkOperatorOrganizationAdministrator,
				networkOperatorThresholdKpiUpdater,
			},
	} = authState;

	const isManagePresetsAccess = networkOperatorOrganizationAdministrator || networkOperatorThresholdKpiUpdater;

	const {
		data: thresholdTechnologies,
		isLoading: isLoadingTechnologies,
	} = useQuery<Array<TTechnologiesValues>>({
		queryKey: [THRESHOLD_TECHNOLOGIES],
		queryFn: getThresholdTechnologies,
		...refetchOptions,
	});

	const {
		data: thresholdVendors,
		isLoading: isLoadingVendors,
	} = useQuery<Array<DefaultVendorOptions>, TSelectOptionType<TVendorsValues>[]>({
		queryKey: [THRESHOLD_VENDORS],
		queryFn: getThresholdVendors,
		select: (vendors) => {
			return vendors.map(item => ({
				isDefault: item.isDefault,
				label: item.vendor.title,
				value: item.vendor.name,
			}));
		},
		...refetchOptions,
	});

	const {
		data: thresholds,
		isLoading: isLoadingThresholds,
		isFetching: isFetchingThresholds,
	} = useQuery<Array<IThreshold>>({
		queryKey: [
			THRESHOLDS,
			{
				vendor,
				technology,
			},
		],
		queryFn: extractQueryParams(getThresholds),
		enabled: [!!vendor, !!technology],
		...refetchOptions,
	});

	const {
		mutate: updateThresholds,
		isLoading: isLoadingUpdateThresholds,
	} = useMutation<IEditThresholdDTO>({
		mutationKey: [EDIT_THRESHOLDS],
		mutationFn: setThresholds,
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.updateThresholds,
				type: ESnackbarTypes.success,
			}));

			return queryClient.invalidateQueries([THRESHOLDS]);
		},
	});

	const {
		mutate: createPresetThreshold,
		isLoading: isLoadingCreatePresetThreshold,
	} = useMutation<Omit<IPresetThresholdCreateDTO, 'name'>>({
		mutationKey: [CREATE_PRESET_THRESHOLDS],
		mutationFn: ({ data, closePresetForm, setNameError }: IPresetThresholdCreateDTO) => {
			return savePresetThreshold(data).then(() => {
				dispatch(setSnackbar({
					message: responseMessages.createPresetByName(data.name),
					type: ESnackbarTypes.success,
				}));

				closePresetForm();

				return queryClient.invalidateQueries([PRESETS_THRESHOLD, { vendor, technology }]);
			}).catch((err) => {
				setNameError(err?.errors[0]?.message || responseMessages.error);
			});
		},
	});

	const {
		mutate: renamePresetThresholdGeneral,
		isLoading: isLoadingEditPresetThreshold,
	} = useMutation<IPresetThresholdUpdateDTO>({
		mutationKey: [EDIT_PRESET_THRESHOLDS],
		mutationFn: ({ name, id, closePresetForm, setNameError, description }: IPresetThresholdUpdateDTO) => {
			return renamePresetThreshold({ name, id, description }).then(() => {
				dispatch(setSnackbar({
					message: responseMessages.editPreset,
					type: ESnackbarTypes.success,
				}));

				closePresetForm();

				return queryClient.invalidateQueries([PRESETS_THRESHOLD, { vendor, technology }]);
			}).catch((err) => {
				setNameError(err?.errors[0]?.message || responseMessages.error);
			});
		},
	});

	const {
		mutate: _renameOranThresholdPreset,
		isLoading: isLoadingEditOranPresetThreshold,
	} = useMutation<IPresetThresholdUpdateDTO>({
		mutationKey: [EDIT_PRESET_THRESHOLDS],
		mutationFn: ({ name, id, closePresetForm, setNameError, description }: IPresetThresholdUpdateDTO) => {
			return renameOranThresholdPreset({ name, id, description }).then(() => {
				dispatch(setSnackbar({
					message: responseMessages.editPreset,
					type: ESnackbarTypes.success,
				}));

				closePresetForm();

				return queryClient.invalidateQueries([PRESETS_THRESHOLD, { vendor, technology }]);
			}).catch((err) => {
				setNameError(err?.errors[0]?.message || responseMessages.error);
			});
		},
	});

	const {
		mutate: editOranThresholdsPreset,
		isLoading: isLoadingUpdateOranPresetThreshold,
	} = useMutation<IUpdateOranThresholdPreset>({
		mutationKey: [EDIT_PRESET_THRESHOLDS],
		mutationFn: async ({
			id,
			data,
			closePresetForm,
			setNameError,
		}: IUpdateOranThresholdPreset) => {
			try {
				await updateOranThresholdPreset({ id, data });

				dispatch(setSnackbar({
					message: responseMessages.editPreset,
					type: ESnackbarTypes.success,
				}));

				await Promise.all([
					queryClient.invalidateQueries([PRESETS_THRESHOLD, { vendor, technology }]),
					queryClient.invalidateQueries([THRESHOLDS, { vendor, technology }]),
				]);

				closePresetForm();
			} catch (err: any) {
				setNameError?.(err?.errors[0]?.message || responseMessages.error);
			}
		},
	});

	const {
		mutate: deletePresetThresholdGeneral,
		isLoading: isLoadingDeletePresetThreshold,
	} = useMutation<{ id: string }>({
		mutationKey: [DELETE_PRESET_THRESHOLDS],
		mutationFn: deleteThresholdPresetAPI,
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.deletePreset,
				type: ESnackbarTypes.success,
			}));

			return queryClient.invalidateQueries([PRESETS_THRESHOLD, { vendor, technology }]);
		},
	});

	const {
		mutate: deleteOranThresholdPreset,
		isLoading: isLoadingDeleteOranPresetThreshold,
	} = useMutation<{ id: string }>({
		mutationKey: [DELETE_PRESET_THRESHOLDS],
		mutationFn: deleteOranThresholdPresetAPI,
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.deletePreset,
				type: ESnackbarTypes.success,
			}));

			return queryClient.invalidateQueries([PRESETS_THRESHOLD, { vendor, technology }]);
		},
	});

	const {
		data: presetsThreshold,
		isLoading: isLoadingPresetsThreshold,
		isFetching: isFetchingPresetsThreshold,
	} = useQuery<Array<IPresetThreshold>, Array<TSelectOptionType<string> & IPresetThreshold>>({
		queryKey: [PRESETS_THRESHOLD, { vendor, technology }],
		queryFn: extractQueryParams(getPresetsThreshold),
		select: (presets) => {
			return presets.map(preset => ({
				...preset,
				value: preset.id,
				label: preset.name,
			}));
		},
		enabled: [!!vendor, !!technology, isManagePresetsAccess],
		...refetchOptions,
		allowedRoles: [
			EUserRoles.networkOperatorThresholdKpiUpdater,
			EUserRoles.networkOperatorOrganizationAdministrator,
		],
	});

	const {
		mutate: exportThresholds,
	} = useMutation<IThresholdDTO>({
		mutationKey: [EXPORT_THRESHOLDS],
		mutationFn: runExportThresholds,
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.exportChartsSuccess,
				type: ESnackbarTypes.success,
			}));
		},
	});

	const {
		mutate: exportOranThresholds,
	} = useMutation<{ fileType: string; vendor: TVendorsValues }>({
		mutationKey: [EXPORT_THRESHOLDS],
		mutationFn: ({ fileType, vendor }: { fileType: string; vendor: TVendorsValues }) => {
			return runExportOranThresholds({ fileType, vendor });
		},
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.exportChartsSuccess,
				type: ESnackbarTypes.success,
			}));
		},
	});

	const isOranVendor = vendor === EVendorsValues.oran;

	return {
		thresholdTechnologies: thresholdTechnologies || [],
		thresholdVendors: thresholdVendors || [],
		thresholds: thresholds || [],
		updateThresholds,
		isLoadingThreshold: (
			(isLoadingThresholds && isFetchingThresholds) ||
			isLoadingUpdateThresholds ||
			isLoadingVendors ||
			isLoadingTechnologies
		),
		createPresetThreshold,
		renamePresetThreshold: isOranVendor ? _renameOranThresholdPreset : renamePresetThresholdGeneral,
		presetsThreshold: presetsThreshold || [],
		isLoadingPresets: (
			isLoadingPresetsThreshold ||
			isLoadingCreatePresetThreshold ||
			isFetchingPresetsThreshold ||
			isLoadingEditPresetThreshold ||
			isLoadingDeletePresetThreshold ||
			isLoadingDeleteOranPresetThreshold ||
			isLoadingEditOranPresetThreshold ||
			isLoadingUpdateOranPresetThreshold
		),
		deletePresetThreshold: isOranVendor ? deleteOranThresholdPreset : deletePresetThresholdGeneral,
		exportThresholds,
		exportOranThresholds,
		editOranThresholdsPreset,
	};
};
